"use client";

import { useMemo } from "react";
import Link from "next/link";
import { Icon, IconProps, Text } from "@stone-payments/jade";
import { Skeleton } from "@/components/skeleton";
import { formatAddress } from "@/utils/formatters";
import { Mask } from "@/utils/masks";
import { getSocialInfo } from "@/utils/social";
import { share } from "@/app/(store)/_utils/share";
import { MessageBuilder } from "@/utils/message-builder";
import { useExperiment } from "@/contexts/analitica";
import { ExperimentsFlags } from "@/services/amplitude/experiment";
import { Container } from "@/components/container";
import { StoreContextType } from "@/contexts/store";

type FooterContactProps = {
  icon: IconProps["use"];
  value: string;
  label: string;
  href?: string | null;
};

type FooterInformationProps = {
  store: StoreContextType["store"];
  loading?: boolean;
};

const FooterContact = ({ icon, value, label, href }: FooterContactProps) => {
  return (
    <div className="flex items-center gap-2 py-1.5 md:px-4">
      {icon ? <Icon use={icon} size="medium" className="leading-0" /> : null}

      <div>
        {href ? (
          <Text variant="text-medium" color="medium" weight="semibold">
            <Link
              href={href}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2"
              aria-label={label}
            >
              {value}
            </Link>
          </Text>
        ) : (
          <Text variant="text-medium" color="medium" weight="semibold">
            {value}
          </Text>
        )}
      </div>
    </div>
  );
};

export const FooterInformation = ({
  store,
  loading,
}: FooterInformationProps) => {
  const showCartExperiment = useExperiment(ExperimentsFlags.ShowCart);
  const formattedAddress = useMemo(
    () =>
      store.vitrine.address?.showAddress
        ? formatAddress(store.vitrine.address)
        : null,
    [store.vitrine.address]
  );
  const contacts = useMemo(() => {
    let items: FooterContactProps[] = [];

    if (store.vitrine.whatsAppSharingEnabled && store.vitrine.whatsAppNumber) {
      items = [
        ...items,
        {
          href: share.createWhatsAppUrl(
            store.vitrine.whatsAppNumber,
            MessageBuilder.buildCatalogGreeting(store.name, showCartExperiment)
          ),
          icon: "brand-whatsapp",
          value: Mask.formatPhoneNumber(store.vitrine.whatsAppNumber),
          label: "WhatsApp",
        },
      ];
    }

    if (store.vitrine.email) {
      items = [
        ...items,
        {
          href: `mailto:${store.vitrine.email}`,
          icon: "mail",
          value: store.vitrine.email,
          label: "Email",
        },
      ];
    }

    const socialItems = Object.entries(store.vitrine.social || {}).reduce<
      FooterContactProps[]
    >((acc, [key, value]) => {
      if (value) {
        const socialInfo = getSocialInfo(
          key as Parameters<typeof getSocialInfo>[0],
          value
        );

        return [...acc, socialInfo];
      }

      return acc;
    }, []);

    return [...items, ...socialItems];
  }, [store.name, store.vitrine, showCartExperiment]);

  if (loading) {
    return (
      <Container>
        <div className="mx-auto grid w-full grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-5 xl:max-w-5xl">
          <div className="col-span-1 lg:col-span-2">
            <Skeleton width="w-14" height="h-4" className="mb-0.5" />

            <Skeleton width="w-50" height="h-5" className="mb-1" />

            <Skeleton width="w-30" height="h-5" />
          </div>

          <div className="col-span-1 grid lg:col-span-3 lg:grid-cols-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="py-1.5">
                <Skeleton width="w-40" height="h-5" />
              </div>
            ))}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mx-auto grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4 lg:grid-cols-5 xl:max-w-5xl">
        {formattedAddress && (
          <div className="col-span-1 lg:col-span-2">
            <Text variant="text-small" color="medium" className="!mb-0.5">
              Endereço
            </Text>

            <Text variant="text-medium" color="medium" weight="semibold">
              {formattedAddress.grouped.address}
            </Text>

            <Text variant="text-medium" color="medium" weight="semibold">
              {formattedAddress.grouped.location}
            </Text>
          </div>
        )}

        <div className="col-span-1 grid lg:col-span-3 lg:grid-cols-2">
          {contacts.map((props) => (
            <FooterContact {...props} key={props.label} />
          ))}
        </div>
      </div>
    </Container>
  );
};
